// ===== frontend/src/components/Form/FormField.jsx =====
import React from 'react';

const SEMANTIC_HINTS = {
  postalCode: { inputMode: 'numeric', autoComplete: 'postal-code' },
  email: { inputMode: 'email', autoComplete: 'email', type: 'email' },
  phoneNumber: { inputMode: 'tel', autoComplete: 'tel' },
  dateOfBirth: { autoComplete: 'bday', placeholder: 'DD.MM.YYYY' },
  givenName: { autoComplete: 'given-name' },
  familyName: { autoComplete: 'family-name' },
  streetAddress: { autoComplete: 'street-address' },
  houseNumber: { autoComplete: 'address-line2' },
  city: { autoComplete: 'address-level2' },
  nationality: { autoComplete: 'country-name' },
  iban: { inputMode: 'text', autoComplete: 'off' },
};

const FormField = ({ field, value, onChange, error }) => {
  const {
    id,
    key: fieldKey,
    label,
    type,
    required,
    placeholder,
    maxLength,
    options,
    validation,
    semantic
  } = field;

  const hints = semantic ? (SEMANTIC_HINTS[semantic] || {}) : {};

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (type === 'checkbox') {
      newValue = e.target.checked ? 'checked' : '';
    }
    
    onChange(id, newValue);
  };

  const renderField = () => {
    switch (type) {
      case 'checkbox':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id={id}
              checked={value === 'checked' || value === true}
              onChange={handleChange}
              style={{
                width: '1rem',
                height: '1rem',
                accentColor: '#2563eb'
              }}
              aria-describedby={error ? `${id}-error` : undefined}
            />
            <label
              htmlFor={id}
              style={{
                fontSize: '0.875rem',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              {label}
            </label>
          </div>
        );

      case 'select':
        return (
          <select
            id={id}
            value={value || ''}
            onChange={handleChange}
            required={required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              color: '#374151'
            }}
            aria-describedby={error ? `${id}-error` : undefined}
          >
            <option value="">Please select...</option>
            {options?.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            id={id}
            value={value || ''}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            maxLength={maxLength}
            rows={4}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        );

      default: // text, email, tel, etc.
        return (
          <input
            type={hints.type || (type === 'text' ? 'text' : type)}
            id={id}
            value={value || ''}
            onChange={handleChange}
            placeholder={placeholder || hints.placeholder || undefined}
            required={required}
            maxLength={maxLength}
            inputMode={hints.inputMode || undefined}
            autoComplete={hints.autoComplete || undefined}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        );
    }
  };

  if (type === 'checkbox') {
    return (
      <div style={{ marginBottom: '1rem' }}>
        {renderField()}
        {error && (
          <p id={`${id}-error`} style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: '500',
          color: '#374151',
          marginBottom: '0.5rem'
        }}
      >
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>}
      </label>
      
      {renderField()}
      
      {error && (
        <p id={`${id}-error`} style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
          {error}
        </p>
      )}
      
      {maxLength && (
        <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
          {value ? value.length : 0}/{maxLength} characters
        </p>
      )}
    </div>
  );
};

export default FormField;
